import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import * as fromCustomization from '@app/client/account/team/customization/reducers/customization.reducer';
import * as fromRoot from '@app/reducers';
import { formatMainDomain, hostnameIsFromMainDomain } from '@app/shared/helpers/location';
import { extractHttpErrorMessage } from '@app/shared/helpers/httpError';
import { getVideoIdFromUrl } from '@app/shared/helpers/urlHelper';
import { Domain } from '@app/shared/models/domain';
import { Provider } from '@app/shared/models/provider';
import { genericRetry } from '@app/shared/operators/genericRetry';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Domain as DomainSdk } from '@smash-sdk/domain/ejs/01-2024/domain';
import GetRecordError from '@smash-sdk/domain/ejs/01-2024/types/GetRecord/GetRecordError';
import ListRecordProvidersError from '@smash-sdk/domain/ejs/01-2024/types/ListRecordProviders/ListRecordProvidersError';
import { Image } from '@smash-sdk/image/ejs/10-2019/image';
import { Request } from 'express';
import { defer, forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, delay, exhaustMap, map, mergeMap, retryWhen, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { environment } from 'src/environments/environment';
import { REQUEST } from '../../../express.tokens';
import { DomainActions } from '../actions';
import { IamService } from '../services/iam.service';
import { ImageService } from '../services/image.service';

@Injectable()
export class DomainEffects {

  loadDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActions.loadDomain),
      withLatestFrom(
        this.store$.select(fromRoot.getTheme),
        this.store$.select(fromRoot.getDevice),
      ),
      exhaustMap(([action, theme, device]) => {
        // TO FIX temp init domain sdk with a host
        const domainSdk = new DomainSdk({ host: environment.domainSdk });
        const reqs: any = [
          defer(() => domainSdk.getRecord({ name: action.id })).pipe(
            genericRetry({
              retryConfig: [
                ...Constant.NetworkErrorsRetryConfiguration,
                { error: GetRecordError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                { error: GetRecordError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                { error: GetRecordError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
              ]
            })
          )
        ];
        // listRecordProvider only if necessary
        if (!hostnameIsFromMainDomain(location.hostname)) {
          reqs.push(
            defer(() => domainSdk.listRecordProviders({ name: action.id })).pipe(
              genericRetry({
                retryConfig: [
                  ...Constant.NetworkErrorsRetryConfiguration,
                  { error: ListRecordProvidersError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                  { error: ListRecordProvidersError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                  { error: ListRecordProvidersError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                ]
              })
            )
          );
        }
        return forkJoin(reqs).pipe(
          mergeMap(([{ record }, ifProvidersRequest]: [{ record: Domain }, { providers: Provider[] }]) => {
            let domain: any;
            const providers = ifProvidersRequest ? [...ifProvidersRequest.providers] : [];
            const copyRecord = Object.assign({}, record);
            // Fallback for logo and background. Mobile uses a portrait-oriented default
            // background; a custom background carried by the record still overrides it
            // (copyRecord is spread last).
            const customizationFallback = device.isMobile
              ? {
                  ...Constant.customizationFallback,
                  background: {
                    ...Constant.customizationFallback.background,
                    sourceUrl: Constant.mobileBackgroundFallbackSourceUrl,
                  },
                }
              : Constant.customizationFallback;
            domain = {
              ...customizationFallback,
              ...copyRecord,
            };

            return [
              DomainActions.loadDomainSuccess({ domain, providers, mainDomain: isPlatformBrowser(this.platformId) ? formatMainDomain() : this.req.baseUrl }),
            ];
          }),
          catchError((error: HttpErrorResponse) => {
            return of(DomainActions.loadDomainFailure({ error: error.message }));
          })
        );
      })
    ));

  updateDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActions.updateDomain),
      withLatestFrom(
        this.store$.select(fromRoot.getActiveTeam),
      ),
      concatMap(([action, teamToUpdate]) => {
        const teamName = action.team.name;
        const newDomainUrl = action.team.domain;
        const domainSdk = new DomainSdk();
        return from(domainSdk.updateDomain({ domainId: teamToUpdate.domain, domain: newDomainUrl })).pipe(
          concatMap(() => {
            return this.iamService.updateTeam(teamToUpdate.id, { name: teamName }).pipe(
              concatMap(() => {
                return of(DomainActions.updateDomainSuccess());
              }));
          }),
          catchError((err: HttpErrorResponse) => {
            const error = err.status;
            return of(DomainActions.updateDomainFailure({ error }));
          })
        );
      }),
    ));

  loadDomainCustomization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActions.loadDomainCustomization),
      withLatestFrom(
        this.store$.select(fromRoot.getActiveTeam),
      ),
      switchMap(([action, team]: any) => {
        if (!team) {
          return [{ type: 'No team selected' }];
        }
        // when team is null, cause an error when user signout
        const domainSdk = new DomainSdk();
        return from(domainSdk.getDomainCustomization({ domainId: team.domain })).pipe(
          map((res) => {
            const customizationFallback = Constant.customizationFallback;
            return DomainActions.loadDomainCustomizationSuccess({ customization: { ...customizationFallback, ...res.customization } });
          }),
          retryWhen(error =>
            error.pipe(
              take(3),
              delay(2000)
            )
          ),
          catchError((error: HttpErrorResponse) => {
            return of(DomainActions.loadDomainCustomizationFailure({ error: error.message }));
          }
          )
        );
      })
    )
  );

  addNewBackgroundImageToDomainCustomization = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActions.addNewBackgroundImageToDomainCustomization),
      map(action => action.image),
      // Each dispatch's create -> upload pipeline (and its catchError) is wrapped in its OWN inner
      // observable here, so an error propagating up through the inner concatMap stages unsubscribes
      // only THIS inner observable, never the outer ofType/map chain. The catchError below used to
      // sit as the LAST operator of one flat outer pipe -- confirmed empirically
      // (domain.effects.spec.ts) to have the same bug already fixed on customization.effects.ts's
      // addTeamBackgroundImageToCreate$ (commits e328ab4f / df2ba51d): RxJS error propagation
      // unsubscribed every operator between the throw and that catchError (including ofType),
      // catchError then replaced the whole errored chain with a one-shot completing observable, and
      // createEffect()'s single subscription to the entire pipe was gone forever -- the first
      // failed upload silently turned this effect into a permanent no-op for the rest of the
      // session, despite the catchError's own comment claiming otherwise.
      concatMap((image: any) => of(image).pipe(
        concatMap(([image]: any[]) => {
          const imageSdk = new Image();
          return forkJoin([of(image), from(imageSdk.createImage())])
        }),
        concatMap(([image, res]: any[]) => forkJoin([of(res), this.imageService.uploadImage(res.image.uploadUrl, image)])),
        concatMap(([res, resUpload]: any[]) => {
          return of(DomainActions.addNewBackgroundImageToDomainCustomizationSuccess({ background: res.image }));
        }),
        catchError((error: any) => of(DomainActions.addNewBackgroundImageToDomainCustomizationFailure({ error: extractHttpErrorMessage(error) })))
      ))
    )
  );

  addNewBackgroundVideoToDomainCustomization = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActions.addNewBackgroundVideoToDomainCustomization),
      map(action => action.url),
      concatMap((url: any) => {
        const video = getVideoIdFromUrl(url);
        return of(DomainActions.addNewBackgroundVideoToDomainCustomizationSuccess({ video }));
      })
    )
  );

  addNewLogoToDomainCustomization = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActions.addNewLogoToDomainCustomization),
      map(action => action.image),
      // Same reasoning, and the same confirmed bug, as addNewBackgroundImageToDomainCustomization$
      // above.
      concatMap((image: any) => of(image).pipe(
        concatMap(([logo]: any[]) => {
          const imageSdk = new Image();
          return forkJoin([of(logo), from(imageSdk.createImage())])
        }),
        concatMap(([logo, { image }]) => {
          return forkJoin([of([logo, { image }]), this.imageService.uploadImage(image.uploadUrl, logo)])
        }),
        concatMap(([[logo, { image }]]) => {
          return [
            DomainActions.addNewLogoToDomainCustomizationSuccess({ logo: image }),
          ];
        }),
        catchError((error: any) => of(DomainActions.addNewLogoToDomainCustomizationFailure({ error: extractHttpErrorMessage(error) })))
      ))
    )
  );

  updateDomainCustomization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainActions.updateDomainCustomization),
      withLatestFrom(
        this.store$.select(fromCustomization.getCustomizationToUpdate),
        this.store$.select(fromRoot.getActiveTeam),
      ),
      switchMap(([action, customizationToUpdate, team]: any) => {
        let logo; let background;
        if (customizationToUpdate.logo && customizationToUpdate.logo.sourceUrl) {
          logo = customizationToUpdate.logo.sourceUrl;
        }
        if (customizationToUpdate.background && customizationToUpdate.background.sourceUrl) {
          background = customizationToUpdate.background.sourceUrl;
        }
        const domainSdk = new DomainSdk();
        return from(domainSdk.updateDomainCustomization({ domainId: team.domain, logo, background })).pipe(
          mergeMap(({ customization }) => {
            return [
              DomainActions.loadDomain({ id: team.domain }),
              DomainActions.updateDomainCustomizationSuccess({ customization })
            ];
          }),
          catchError((error: any) => {
            return of(DomainActions.updateDomainCustomizationFailure({ error: error.message }));
          }
          )
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<fromCustomization.State & fromRoot.State>,
    private imageService: ImageService,
    private iamService: IamService,
    @Optional() @Inject(REQUEST) private req: Request,
    @Inject(PLATFORM_ID) private platformId: any
  ) { }

}
